// frontend/src/lib/motion.ts
// Shared reduced-motion helpers.
//
// The global stylesheet (colors_and_type.css) forces every CSS-declared
// animation/transition duration to ~0 and `scroll-behavior` to `auto` under
// `prefers-reduced-motion: reduce`. Per spec that rule only governs scrolls
// whose script-requested behavior is `'auto'` or unspecified: an explicit
// `behavior: 'smooth'` passed to scrollIntoView/scrollBy/scrollTo wins
// regardless, and a JS rAF loop that writes styles directly (e.g. clipPath)
// is invisible to CSS entirely. Both cases have to check this themselves.

/**
 * The one spelling of the query. It was written out separately in five places,
 * which is four chances to typo a media query that fails silently: an
 * unrecognised feature name never matches, so the animation simply keeps
 * playing and nothing tells you.
 *
 * Components that need to re-render when the preference changes pass this to
 * the `useMatchMedia` hook; one-shot callers use `prefersReducedMotion()`.
 */
export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Whether the user has asked for reduced motion. Guards `window.matchMedia`
 * being undefined (SSR, jsdom) rather than throwing.
 */
export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

// Downgrades an explicit 'smooth' request to 'auto' when reduced motion is
// on; leaves 'auto'/undefined alone (nothing to downgrade).
function gentleBehavior(behavior?: ScrollBehavior): ScrollBehavior | undefined {
  return behavior === 'smooth' && prefersReducedMotion() ? 'auto' : behavior;
}

/** scrollIntoView that respects prefers-reduced-motion. */
export function scrollIntoViewGently(el: Element, options: ScrollIntoViewOptions = {}): void {
  el.scrollIntoView({ ...options, behavior: gentleBehavior(options.behavior) });
}

/** scrollBy that respects prefers-reduced-motion. */
export function scrollByGently(el: Element, options: ScrollToOptions): void {
  el.scrollBy({ ...options, behavior: gentleBehavior(options.behavior) });
}
